/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButton, IButtonProps } from '@fluentui/react/lib/Button';
import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { IFocusZone } from '@fluentui/react/lib/FocusZone';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';

export type LuisContextualMenuProps = {
    /**
     * The items that can be displayed in the contextual menu.
     */
    items: IContextualMenuItem[];

    /**
     * Boolean to indicate if focus should go for the menu.
     */
    isFocused: boolean;

    /**
     * Boolean to indicate if the context menu open or not.
     */
    isVisible: boolean;

    /**
     * A callback when the menu is dismissed
     */
    onDismiss: (event: React.MouseEvent | React.KeyboardEvent) => void;

    /**
     * A callback when the menu fires a key up.
     */
    onKeyUp?: (event: React.KeyboardEvent) => void;

    /**
     * Optional props to send to the button
     * that opens the menu.
     */
    menuTriggerProps?: Partial<IButtonProps>;

    /**
     * Optional props to pass to the underlying
     * contextual menu
     */
    contextualMenuProps?: Partial<IContextualMenuProps>;
};

const { getClassName } = getStylist('LuisContextualMenu');
const calloutTheme = getScopedTheme('LuisContextualMenu');

const menuBtn = getClassName('MenuBtnClass', { width: 0, height: '100%', visibility: 'hidden', padding: 0, minWidth: 0, border: 'none' });
const calloutStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: calloutTheme.maxHeight }
});

/**
 * Handles the representation of the contextual menu
 * that appears whenever the cursor is focused inside
 * LuisContextualMenuInput text field.
 */
export const LuisContextualMenu = (props: LuisContextualMenuProps) => {
    const { items, isVisible, isFocused, onKeyUp, onDismiss, menuTriggerProps, contextualMenuProps } = props;
    const buttonRef = React.useRef<IButton>(null);
    const focusZone = React.useRef<IFocusZone>(null);

    React.useEffect(() => {
        if (isVisible) {
            buttonRef.current.openMenu(false, isFocused);
        } else {
            buttonRef.current.dismissMenu();
        }
    }, [isVisible]);

    React.useEffect(() => {
        if (isVisible && focusZone.current) {
            focusZone.current.focus();
        }
    }, [isFocused]);

    return (
        <DefaultButton
            componentRef={buttonRef}
            disabled
            className={menuBtn}
            {...menuTriggerProps}
            role="menuitem"
            menuProps={{
                items,
                isBeakVisible: false,
                ...contextualMenuProps,
                focusZoneProps: { componentRef: focusZone },
                calloutProps: {
                    ...contextualMenuProps?.calloutProps,
                    onKeyUp,
                    styles: { ...calloutStyles, ...contextualMenuProps?.calloutProps?.styles }
                },
                onDismiss
            }}
        />
    );
};
